import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import dayjs from 'dayjs';
import { SilentError } from 'init/silent-error';
import { isDefined } from 'remeda';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { PaginationSchemaV2 } from 'types/pagination';
import { z } from 'zod/v4';

const URL = '/custom_variables';

export const VarTypeSchema = z.enum(['string', 'number', 'boolean', 'date']);
export type VarType = z.infer<typeof VarTypeSchema>;

export const ModelTypeSchema = z.enum(['invoice', 'debtor']);
export type ModelType = z.infer<typeof ModelTypeSchema>;

export const DisplayTypeSchema = z.enum(['money', 'money_in_cents', 'percentage']);
export type DisplayType = z.infer<typeof DisplayTypeSchema>;

const baseCustomVariableFields = {
  id: z.number(),
  name: z.string(),
  columnName: z.string(),
  modelType: ModelTypeSchema,
  api: z.boolean(),
  webhook: z.boolean(),
  display: DisplayTypeSchema.nullish(),
};

export const CustomVariableSchema = z.discriminatedUnion('varType', [
  z.object({
    ...baseCustomVariableFields,
    varType: z.literal('string'),
    defaultValue: z.string().nullish(),
  }),
  z.object({
    ...baseCustomVariableFields,
    varType: z.literal('number'),
    defaultValue: z.union([z.string(), z.number()]).nullish(),
  }),
  z.object({
    ...baseCustomVariableFields,
    varType: z.literal('boolean'),
    defaultValue: z.union([z.boolean(), z.string()]).nullish(),
  }),
  z.object({
    ...baseCustomVariableFields,
    varType: z.literal('date'),
    // Blank/whitespace-only date defaults mean "no default" — coerce to null so a
    // single empty value doesn't fail validation for the whole list.
    defaultValue: z.preprocess(
      value => (typeof value === 'string' && value.trim() === '' ? null : value),
      z
        .string()
        .transform(dayjs)
        .refine(d => d.isValid(), 'Invalid date')
        .nullish(),
    ),
  }),
]);

export type CustomVariable = z.infer<typeof CustomVariableSchema>;

const LoadCustomVariablesResponseSchema = z.object({
  data: z.array(CustomVariableSchema),
  pagination: PaginationSchemaV2,
});

export type LoadCustomVariablesResponse = z.infer<typeof LoadCustomVariablesResponseSchema>;

export type LoadCustomVariablesParams = {
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
};

export async function loadCustomVariablesQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  params: LoadCustomVariablesParams,
) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL, { params });

  const parseResult = LoadCustomVariablesResponseSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'customVariables');
    throw new SilentError();
  }

  return parseResult.data;
}

const emptyCustomVariables: LoadCustomVariablesResponse = {
  data: [],
  pagination: { totalItems: 0 },
};

export function useLoadCustomVariables(
  params: LoadCustomVariablesParams = {},
  options: QueryOptions<LoadCustomVariablesResponse> = {},
) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<LoadCustomVariablesResponse, ApiError>({
    queryKey: ['customVariables', params],
    queryFn: () => loadCustomVariablesQueryFn(axiosInstance, params),
    placeholderData: previousData => {
      if (isDefined(previousData)) return previousData;
      return emptyCustomVariables;
    },
    ...options,
  });

  return addResourceNameToQueryResult<
    LoadCustomVariablesResponse,
    ApiError,
    'customVariables',
    'PreventUndefined'
  >('customVariables', queryResult, emptyCustomVariables);
}
